import { useMemo, useRef } from "react";

import useDidUpdateEffect from "@hooks/useDidUpdateEffect";
import useOrderFieldsLoading from "../../context/useOrderFieldsLoading";

import { OrderFields } from "../../constants";
import useOrderData from "../../context/useOrderData";
import useOrderFormik from "../useOrderFormik";
import { useRouter } from "@lib/router";
import isCollectionRequest from "../../utils/isCollectionRequest";

const getCompareValue = (values: any[]) => values.join(",");

// Hook listens for changes of starting country, destination country or package size fields
// and reloads offers list depending on new values

const useOffersLoader = (): void => {
  const { values } = useOrderFormik();

  const { updateOffers } = useOrderData();

  const { isFieldLoading } = useOrderFieldsLoading();

  const prevCompareValueRef = useRef("");

  const { defaultLocale } = useRouter();

  const countryFrom = values[OrderFields.STARTING_COUNTRY] || undefined;
  const countryTo = values[OrderFields.DESTINATION_COUNTRY] || undefined;
  const deliveryMethod = values[OrderFields.DELIVERY_METHOD] || undefined;
  const packageSizeTitle = values[OrderFields.PACKAGE_SIZE] || undefined;
  const senderZipCode = values[OrderFields.SENDER_ZIP_CODE] || undefined;
  const receiverZipCode = values[OrderFields.RECEIVER_ZIP_CODE] || undefined;
  const affiliateId = values[OrderFields.AFFILIATE_ID] || undefined;
  const shipmentId = values[OrderFields.SHIPMENT_ID] || undefined;
  const senderPhoneNumber = values[OrderFields.SENDER_PHONE_NUMBER] || undefined;
  const senderEmail = values[OrderFields.SENDER_EMAIL] || undefined;
  const promoCode = values[OrderFields.PROMO_CODE] || undefined;
  const offerGroupId = values[OrderFields.OFFER_GROUP_ID] || undefined;

  const isPackageSizesLoading = isFieldLoading(OrderFields.PACKAGE_SIZE);
  const printLabelType = values[OrderFields.LABEL_PRINT] || undefined;

  const useCr = isCollectionRequest(defaultLocale, deliveryMethod, printLabelType);

  const compareValue = useMemo(
    () =>
      getCompareValue([
        countryFrom,
        countryTo,
        packageSizeTitle,
        senderZipCode,
        receiverZipCode,
        affiliateId,
        shipmentId,
        isPackageSizesLoading,
        printLabelType,
      ]),
    [
      countryFrom,
      countryTo,
      packageSizeTitle,
      senderZipCode,
      receiverZipCode,
      affiliateId,
      shipmentId,
      isPackageSizesLoading,
      printLabelType,
    ]
  );

  // We can ignore promo code change, but still have sent it to offers endpoint
  useDidUpdateEffect(() => {
    if (prevCompareValueRef.current === compareValue) {
      return;
    }
    prevCompareValueRef.current = compareValue;
    updateOffers({
      countryFrom,
      countryTo,
      deliveryMethod,
      packageSizeTitle,
      promoCode,
      senderZipCode,
      receiverZipCode,
      affiliateId,
      shipmentId,
      senderPhoneNumber,
      senderEmail,
      offerGroupId,
      useCr,
    });
  }, [
    compareValue,
    countryFrom,
    countryTo,
    deliveryMethod,
    packageSizeTitle,
    promoCode,
    senderZipCode,
    receiverZipCode,
    affiliateId,
    shipmentId,
    updateOffers,
    senderPhoneNumber,
    senderEmail,
    offerGroupId,
    useCr,
    isPackageSizesLoading,
  ]);
};
export default useOffersLoader;
